/**
 * Piezas de interfaz reutilizables.
 *
 * Todas respetan dos reglas que atraviesan la aplicación:
 *
 *  · **El modo discreto oculta los importes.** La clase `ed-money` es la que
 *    activa el difuminado, así que cualquier cifra que deba ocultarse lleva esa
 *    clase. No hay que acordarse en cada pantalla: se usa `<Money>` y ya está.
 *  · **Todo lo táctil mide 44 px como mínimo**, que es el mínimo cómodo para el
 *    dedo. En una aplicación que se usa de pie en la caja de un supermercado, eso
 *    no es un detalle estético.
 */

import { AlertCircle, Inbox, Loader2, X } from 'lucide-react';
import { useEffect, useId, useRef, type ReactNode } from 'react';

import { formatCOP } from '@eldescuadre/core';

import { useSession } from '../lib/session';

/**
 * Un importe en pesos.
 *
 * Con `signed` se muestra el signo y el color según si suma o resta, que es lo que
 * permite leer una lista de movimientos de un vistazo.
 */
export function Money({
  amount,
  signed = false,
  className = '',
  as = 'span',
}: {
  amount: number;
  signed?: boolean;
  className?: string;
  as?: 'span' | 'div' | 'strong';
}): React.JSX.Element {
  const stealth = useSession((s) => s.stealth);
  const Tag = as;

  if (stealth) {
    return (
      <Tag className={`ed-money ${className}`} data-sensitive aria-label="Importe oculto">
        ••••••
      </Tag>
    );
  }

  const sign = signed ? (amount > 0 ? '+' : amount < 0 ? '−' : '') : amount < 0 ? '−' : '';
  const tone = signed
    ? amount > 0
      ? 'text-ed-success'
      : amount < 0
        ? 'text-ed-coral-light'
        : 'text-ed-text-silver'
    : '';

  return (
    <Tag className={`ed-money tabular-nums ${tone} ${className}`}>
      {sign}
      {formatCOP(Math.abs(amount))}
    </Tag>
  );
}

/** Tarjeta con título opcional. */
export function Card({
  title,
  action,
  children,
  className = '',
}: {
  title?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}): React.JSX.Element {
  return (
    <section className={`ed-card p-4 ${className}`}>
      {(title ?? action) && (
        <header className="mb-3 flex items-center justify-between gap-3">
          {title && (
            <h2 className="font-display text-sm font-bold uppercase tracking-wider text-ed-text-silver">
              {title}
            </h2>
          )}
          {action}
        </header>
      )}
      {children}
    </section>
  );
}

/** Mensaje de error con explicación y, si toca, qué hacer. */
export function ErrorNote({
  message,
  hint,
}: {
  message: string;
  hint?: string;
}): React.JSX.Element {
  return (
    <div
      role="alert"
      className="flex items-start gap-2 rounded-ed border border-ed-coral-deep/50 bg-ed-coral-deep/15 px-3 py-2 text-sm text-ed-coral-light"
    >
      <AlertCircle size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
      <span>
        {message}
        {hint && <span className="mt-1 block text-xs text-ed-coral-light/80">{hint}</span>}
      </span>
    </div>
  );
}

/** Aviso positivo o informativo. */
export function Note({ children }: { children: ReactNode }): React.JSX.Element {
  return (
    <p className="rounded-ed border border-ed-cyan/25 bg-ed-cyan/10 px-3 py-2 text-xs text-ed-cyan-light">
      {children}
    </p>
  );
}

/** Estado vacío. Siempre dice qué hacer a continuación, nunca solo «no hay datos». */
export function EmptyState({
  title,
  message,
  action,
}: {
  title: string;
  message: string;
  action?: ReactNode;
}): React.JSX.Element {
  return (
    <div className="flex flex-col items-center gap-2 px-4 py-8 text-center">
      <Inbox size={32} className="text-ed-text-muted" aria-hidden="true" />
      <p className="font-display text-sm font-bold text-ed-text-white">{title}</p>
      <p className="max-w-sm text-sm text-ed-text-muted">{message}</p>
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}

/** Rueda de carga con texto, para que se sepa qué se está esperando. */
export function Loading({ label = 'Cargando…' }: { label?: string }): React.JSX.Element {
  return (
    <p className="flex items-center justify-center gap-2 py-6 text-sm text-ed-text-muted">
      <Loader2 size={16} className="animate-spin" aria-hidden="true" />
      {label}
    </p>
  );
}

/**
 * Ventana emergente.
 *
 * Atrapa el foco, se cierra con Escape y bloquea el desplazamiento del fondo.
 * Todo eso no es adorno: sin ello, en el móvil el fondo se desplaza detrás de la
 * ventana y el usuario acaba perdido.
 */
export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
}): React.JSX.Element | null {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);

    // El foco entra en la ventana al abrirse.
    const firstField = panelRef.current?.querySelector<HTMLElement>(
      'input, select, textarea, button',
    );
    firstField?.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center">
      <div
        className="absolute inset-0 bg-ed-bg-abyss/75 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="ed-safe-bottom relative flex max-h-[92dvh] w-full max-w-lg flex-col overflow-hidden rounded-t-2xl border border-ed-bg-border/50 bg-ed-bg-primary shadow-ed-elevated sm:rounded-ed"
      >
        <header className="flex items-center justify-between gap-3 border-b border-ed-bg-border/30 px-4 py-3">
          <h2 id={titleId} className="font-display text-base font-bold">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="ed-btn-ghost !min-h-[40px] !px-2"
            aria-label="Cerrar"
          >
            <X size={18} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-4 py-4">{children}</div>

        {footer && (
          <footer className="border-t border-ed-bg-border/30 px-4 py-3">{footer}</footer>
        )}
      </div>
    </div>
  );
}

/** Campo de formulario con etiqueta y error visibles. */
export function Field({
  label,
  error,
  hint,
  children,
  htmlFor,
}: {
  label: string;
  error?: string | undefined;
  hint?: string | undefined;
  children: ReactNode;
  htmlFor?: string;
}): React.JSX.Element {
  return (
    <div className="space-y-1.5">
      <label className="ed-label block" htmlFor={htmlFor}>
        {label}
      </label>
      {children}
      {hint && !error && <p className="text-xs text-ed-text-muted">{hint}</p>}
      {error && (
        <p className="text-xs text-ed-coral-light" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

/** Botón de envío que muestra que está trabajando. */
export function SubmitButton({
  children,
  pending,
  disabled,
  variant = 'primary',
  type = 'submit',
  onClick,
}: {
  children: ReactNode;
  pending?: boolean;
  disabled?: boolean;
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost';
  type?: 'submit' | 'button';
  onClick?: () => void;
}): React.JSX.Element {
  const variantClass =
    variant === 'primary'
      ? 'ed-btn-primary'
      : variant === 'secondary'
        ? 'ed-btn-secondary'
        : variant === 'danger'
          ? 'ed-btn-danger'
          : 'ed-btn-ghost';

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={pending || disabled}
      className={`${variantClass} w-full`}
    >
      {pending && <Loader2 size={16} className="animate-spin" aria-hidden="true" />}
      {children}
    </button>
  );
}

/** Barra de progreso accesible. */
export function Progress({
  percent,
  tone = 'gold',
  label,
}: {
  percent: number;
  tone?: 'gold' | 'success' | 'coral' | 'cyan';
  label?: string;
}): React.JSX.Element {
  const clamped = Math.max(0, Math.min(100, percent));
  const color =
    tone === 'success'
      ? 'bg-ed-success'
      : tone === 'coral'
        ? 'bg-ed-coral'
        : tone === 'cyan'
          ? 'bg-ed-cyan'
          : 'bg-ed-gold';

  return (
    <div
      className="h-2 w-full overflow-hidden rounded-full bg-ed-bg-abyss"
      role="progressbar"
      aria-valuenow={Math.round(clamped)}
      aria-valuemin={0}
      aria-valuemax={100}
      {...(label ? { 'aria-label': label } : {})}
    >
      <div className={`h-full rounded-full ${color} transition-[width]`} style={{ width: `${clamped}%` }} />
    </div>
  );
}

/** Bloque de cifra con su etiqueta, para los paneles. */
export function Stat({
  label,
  value,
  hint,
  tone = 'neutral',
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  tone?: 'neutral' | 'positive' | 'negative' | 'gold';
}): React.JSX.Element {
  const toneClass =
    tone === 'positive'
      ? 'text-ed-success'
      : tone === 'negative'
        ? 'text-ed-coral-light'
        : tone === 'gold'
          ? 'text-ed-gold'
          : 'text-ed-text-white';

  return (
    <div className="rounded-ed border border-ed-bg-border/30 bg-ed-bg-elevated/40 p-3">
      <dt className="text-xs uppercase tracking-wide text-ed-text-muted">{label}</dt>
      <dd className={`mt-1 font-display text-lg font-bold ${toneClass}`}>{value}</dd>
      {hint && <p className="mt-0.5 text-xs text-ed-text-muted">{hint}</p>}
    </div>
  );
}
